import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Small "i" info pill — click opens a centered popover explaining how a field
 * impacts the analysis. Ported from the AI Readiness `info-tip` pattern:
 * click-to-open (not hover), overlay + Escape/×/overlay-click to close, and the
 * popover is portaled to <body> so fixed positioning isn't clipped by sticky /
 * transformed / overflow ancestors (e.g. modals, sticky headers).
 */
interface InfoTipProps {
  title: string;
  children: React.ReactNode;
  /** Optional text shown beside the "i". */
  label?: string;
}

const InfoTip: React.FC<InfoTipProps> = ({ title, children, label }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        title="How this affects the analysis"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          marginLeft: 6,
          background: '#eef4ff',
          border: '1px solid #c5d9f5',
          borderRadius: 20,
          color: '#1976d2',
          cursor: 'pointer',
          padding: label ? '2px 9px 2px 6px' : 2,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1,
          verticalAlign: 'middle',
        }}
      >
        <span
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: 12,
            fontWeight: 700,
            width: 15,
            height: 15,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: '1.5px solid #1976d2',
          }}
        >
          i
        </span>
        {label && <span style={{ fontFamily: 'inherit', fontStyle: 'normal' }}>{label}</span>}
      </button>

      {open &&
        createPortal(
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,23,42,0.38)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={title}
              style={{
                width: 'min(460px, 96vw)',
                maxHeight: '80vh',
                overflowY: 'auto',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                zIndex: 1401,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 18px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#fff',
                  borderRadius: '12px 12px 0 0',
                }}
              >
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                  {title}
                </h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  style={{
                    border: 'none',
                    background: '#fee2e2',
                    color: '#b91c1c',
                    borderRadius: 8,
                    width: 30,
                    height: 30,
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <div
                style={{ padding: '14px 18px', fontSize: 13, lineHeight: 1.6, color: '#374151' }}
              >
                {children}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default InfoTip;
